import React, { useEffect, useState } from "react";
import {
  Card,
  Typography,
  Table,
  Spin,
  Tag,
  Divider,
  Progress,
  Space,
} from "antd";
import {
  TrophyOutlined,
  BarChartOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useSelector } from "react-redux";

const { Title, Text } = Typography;

const getHonorTitle = (rank) => {
  switch (rank) {
    case 0:
      return "Summa Cum Laude";
    case 1:
      return "Magna Cum Laude";
    case 2:
      return "Cum Laude";
    case 3:
      return "Dean’s Lister";
    case 4:
      return "Honor Mention";
    case 5:
      return "Distinguished Iskolar";
    case 6:
      return "Notable Iskolar";
    case 7:
      return "Proficient Iskolar";
    case 8:
      return "Rising Iskolar";
    case 9:
      return "Iskalert";
    default:
      return null;
  }
};

const getHonorColor = (rank) => {
  switch (rank) {
    case 0:
      return "gold";
    case 1:
      return "silver";
    case 2:
      return "volcano";
    case 3:
      return "geekblue";
    case 4:
      return "blue";
    case 5:
      return "purple";
    case 6:
      return "cyan";
    case 7:
      return "green";
    case 8:
      return "lime";
    case 9:
      return "magenta";
    default:
      return "default";
  }
};

const getMedal = (rank) => {
  switch (rank) {
    case 0:
      return <Tag color="gold">🥇</Tag>;
    case 1:
      return <Tag color="silver">🥈</Tag>;
    case 2:
      return <Tag color="volcano">🥉</Tag>;
    default:
      return <Tag>{rank + 1}</Tag>;
  }
};

const getRiskColor = (label) => {
  switch (label) {
    case "Excellent":
      return "green";
    case "Good":
      return "blue";
    case "Needs Attention":
      return "orange";
    case "Failing":
      return "red";
    default:
      return "gray";
  }
};

const PhishingLeaderboardPage = () => {
  const { details } = useSelector((state) => state.accountDetailSlice);
  const isAuthenticated = !!localStorage.getItem("api_token");
  const user = { email: details?.email };
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [riskScore, setRiskScore] = useState(null);
  const [userRank, setUserRank] = useState(null);
  const [userScore, setUserScore] = useState(null);

  const API_BASE_URL =
    process.env.NODE_ENV === "production"
      ? process.env.REACT_APP_API_URL
      : "http://localhost:3001/api";

  useEffect(() => {
    axios.get(`${API_BASE_URL}/leaderboard`).then((res) => {
      const sorted = [...(res.data?.leaderboard || [])].sort((a, b) => {
        if (b.score === a.score) {
          if (a.fastest_reporting_speed === null) return 1;
          if (b.fastest_reporting_speed === null) return -1;
          return a.fastest_reporting_speed - b.fastest_reporting_speed;
        }
        return b.score - a.score;
      });

      setLeaderboard(sorted);

      if (isAuthenticated && user?.email) {
        const currentUserIndex = sorted.findIndex(
          (e) => e.email === user.email
        );
        if (currentUserIndex !== -1) {
          setUserRank(currentUserIndex + 1);
          setUserScore(sorted[currentUserIndex].score);
        }
      }

      setLoading(false);
    });

    if (isAuthenticated && user?.email) {
      axios
        .get(`${API_BASE_URL}/metrics`, { params: { email: user.email } })
        .then((res) => setRiskScore(res.data?.cumulative_risk_score ?? 0))
        .catch(() => setRiskScore(0));
    }
  }, [isAuthenticated, user]);

  const riskLabel = (() => {
    if (riskScore >= 90) return "Excellent";
    if (riskScore >= 75) return "Good";
    if (riskScore >= 60) return "Needs Attention";
    return "Failing";
  })();
  const riskColor = getRiskColor(riskLabel);

  const columns = [
    {
      title: "Rank",
      dataIndex: "rank",
      key: "rank",
      render: (_, __, index) => getMedal(index),
      width: 70,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, record, index) => {
        const honor = getHonorTitle(index);
        const color = getHonorColor(index);
        return (
          <span style={{ fontWeight: index < 3 ? "bold" : "normal" }}>
            {text} {honor && <Tag color={color}>{honor}</Tag>}
          </span>
        );
      },
    },
    {
      title: "Score",
      dataIndex: "score",
      key: "score",
      sorter: (a, b) => b.score - a.score,
    },
  ];

  return (
    <div style={{ display: "flex", justifyContent: "center", padding: "24px" }}>
      <Card
        bordered
        style={{
          width: 400,
          textAlign: "center",
          borderRadius: 16,
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <TrophyOutlined
              style={{ fontSize: 28, color: "#faad14", marginRight: 8 }}
            />
            <Title level={4} style={{ margin: 0 }}>
              Iskalert Leaderboard
            </Title>
          </div>
        }
      >
        {riskScore !== null && (
          <Card bordered style={{ margin: "16px 0", textAlign: "center" }}>
            {userRank && (
              <div style={{ marginTop: 8 }}>
                <Text strong>Your Rank: {userRank}</Text> <br />
                <Text strong>Score: {userScore}</Text>
              </div>
            )}
          </Card>
        )}

        <Divider />

        {loading ? (
          <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
        ) : (
          <Table
            dataSource={leaderboard}
            columns={columns}
            rowKey={(record) => record.email}
            pagination={false}
            size="small"
            bordered={false}
          />
        )}
      </Card>
    </div>
  );
};

export default PhishingLeaderboardPage;
